import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { promiseWithCatch, safelyDetectChanges, scrollToElement, unsubscribeAll } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import { CommentPositionNotFoundError } from 'src/app/common/exceptions/comment-position-not-found';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { SendMessageFormService } from 'src/app/forms/conversation-forms/send-message-form.service';
import { TranslateParams } from 'src/app/interfaces';
import {
  AddCommentContentWithPositionParent,
  CommentPosition,
  LoadedCommentsState,
  makeDefaultLoadedCommentsState,
} from 'src/app/interfaces/posts.interface';
import { EntityStatus } from 'src/app/interfaces/views.interface';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import {
  CommentPosition as AbsoluteCommentPosition,
  AccountStatus,
  Post,
  SocialContext,
  SocialContextEntityType,
  UserRelationToGroup,
} from 'src/app/swagger-api';
import { MaybeNull } from 'src/app/utils';
import { getStatus } from '../../profile/user-profile/view-profile/regular-user-profile/regular-user-profile-helper';
import { PostsService } from '../posts.service';
import { CommentsPaginationComponent } from './comments/comments-pagination/comments-pagination.component';
import { CommentsBaseService } from './comments/comments.base.service';
import { PostDetailCommentsService } from './post-detail.comments.service';

@Component({
  selector: 'app-post-detail',
  templateUrl: './post-detail.component.html',
  styleUrls: ['./post-detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PostDetailComponent implements OnInit, OnDestroy, IDeactivate {
  @Input({ required: true }) public post: Post;

  public loadedComments: LoadedCommentsState = makeDefaultLoadedCommentsState();
  public isAuthenticated = false;
  public isAdminSession = false;
  public isLeader = false;
  public isFormChanged = false;
  public focusCommentId: MaybeNull<string> = null;
  public hiddenCommentHint = '';

  public readonly DIGITS_REPRESENTATION_OPTIONS: string = AppValues.DIGITS_REPRESENTATION_OPTIONS;
  public readonly DATE_TIME_FORMAT_LONG: string = AppValues.DATE_TIME_FORMAT_LONG;
  public readonly LINK_POSTS: string = ProjectLinks.LINK_POSTS;
  public readonly DELETED: AccountStatus = AccountStatus.DELETED;

  private subscriptions$: Subscription[] = [];

  private postDetailCommentsService = inject(PostDetailCommentsService);
  private sessionService = inject(SessionService);
  private postsServise = inject(PostsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private sendMessageFormService = inject(SendMessageFormService);
  private operationInfoService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);

  public ngOnInit(): void {
    this.subscribeToUrlFragmentChanges();
    this.subscribeToPostCommentsChanges();
    this.subscribeToSessionChanges();

    this.isLeader = this.post.social_context.actor_role === UserRelationToGroup.Leader;

    // setting post should be before reload comments
    this.postDetailCommentsService.setPostInformation(this.post);
    this.reloadComments();
    this.updateCommentHint();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public get getStatus(): EntityStatus {
    return getStatus(this.post.creator);
  }

  public buildContextEntityLink(context: SocialContext): string[] {
    let link: string;

    switch (context.entity_type) {
      case SocialContextEntityType.Project:
        link = ProjectLinks.LINK_PROJECTS_VIEW;
        break;
      case SocialContextEntityType.Community:
        link = ProjectLinks.LINK_COMMUNITY_VIEW;
        break;
      case SocialContextEntityType.CharityCommunity:
        link = ProjectLinks.LINK_CHARITY_VIEW;
        break;
      case SocialContextEntityType.HealthCommunity:
        link = ProjectLinks.LINK_COMMUNITY_VIEW;
        break;
      default:
        throw new Error(`Unknown entity type: ${context.entity_type}`);
    }

    return [link, context.entity_id];
  }

  public onAddComment(info: AddCommentContentWithPositionParent): void {
    const indexLabel: string = info.parentCommentPosition
      ? info.parentCommentPosition.indexRoot.toString()
      : CommentsPaginationComponent.TOP_LABEL;

    const indexButton: string = info.parentCommentPosition
      ? info.parentCommentPosition.indexReply
        ? info.parentCommentPosition.indexRoot.toString() + '-' + info.parentCommentPosition.indexReply.toString()
        : info.parentCommentPosition.indexRoot.toString()
      : CommentsPaginationComponent.TOP_LABEL;

    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: 'btn-send-message-' + indexButton },
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: 'comment-load-more-btn-' + indexLabel },
          { elementId: 'comment-load-all-btn-' + indexLabel },
        ],
      },
      () => {
        this.postDetailCommentsService.addOrReplyComment(info.sendMessageData);
      },
    );
  }

  public onRemoveComment(commentPosition: CommentPosition): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        successfulMessage: 'POSTS_CONFIRM_REMOVE_HINT',
        confirmationDialog: {
          description: 'POSTS_REMOVE_COMMENT_DESCRIPTION',
          title: 'POSTS_REMOVE_POST_COMMENT_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'POSTS_REMOVE_COMMENT_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.postDetailCommentsService.removeComment(commentPosition);
      },
    );
  }

  public onLoadMoreComments(parentCommentPosition: CommentPosition | null): void {
    if (parentCommentPosition) {
      this.postDetailCommentsService.loadMoreReplies(parentCommentPosition);
    } else {
      this.postDetailCommentsService.loadMoreRootComments();
    }
  }

  public onLoadAllComments(parentCommentPosition: CommentPosition | null): void {
    if (parentCommentPosition) {
      this.postDetailCommentsService.loadAllRemainingReplies(parentCommentPosition);
    } else {
      this.postDetailCommentsService.loadAllRemainingRootComments();
    }
  }

  public editPost(): void {
    promiseWithCatch(this.router.navigate([`${ProjectLinks.LINK_POSTS_UPDATE}/${this.post.entity_id}`]));
  }

  public onRemovePost(): void {
    this.operationInfoService.setupOperation(
      {
        successfulMessage: 'POST_CONFIRM_REMOVE_HINT',
        confirmationDialog: {
          description: 'POSTS_REMOVE_POST_DESCRIPTION',
          title: 'POSTS_REMOVE_POST_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'POSTS_REMOVE_POST_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.postsServise.removePost({ postId: this.post.entity_id });
      },
    );
  }

  public setFormStatus(isFormChanged: boolean): void {
    this.isFormChanged = isFormChanged;
    safelyDetectChanges(this.cdr);
  }

  public setDefaultValueForModal(): void {
    this.sendMessageFormService.resetFormStatus();
  }

  private focusComment(): void {
    setTimeout(() => {
      scrollToElement(this.focusCommentId);
      this.focusCommentId = null;
    });
  }

  private subscribeToPostCommentsChanges(): void {
    this.subscriptions$.push(
      this.postDetailCommentsService.commentsChanges.subscribe((loadedComments: LoadedCommentsState) => {
        this.loadedComments = loadedComments;
        this.cdr.detectChanges();

        if (this.focusCommentId) {
          this.focusComment();
        }
      }),
    );
  }

  private fetchInitialCommentsByPosition(commentId: string): void {
    const subscription$: Subscription = this.postDetailCommentsService.getCommentPosition(commentId).subscribe({
      next: (position: AbsoluteCommentPosition) => {
        subscription$.unsubscribe();

        // If the comment is already 'included' in the first page, don't ask for less. Same applies to replies.
        const commentLimit: number =
          position.base_comment_position + 1 > CommentsBaseService.COMMENTS_PER_PAGE
            ? position.base_comment_position + 1
            : CommentsBaseService.COMMENTS_PER_PAGE;

        const replyLimit: number =
          position.reply_comment_position &&
          position.reply_comment_position + 1 > CommentsBaseService.COMMENT_REPLIES_PER_PAGE
            ? position.reply_comment_position + 1
            : CommentsBaseService.COMMENT_REPLIES_PER_PAGE;

        this.postDetailCommentsService.loadInitialComments(commentLimit, replyLimit);
      },
      error: (_: CommentPositionNotFoundError) => {
        // If we can't get the position of the focused comment (because it was deleted, etc...)
        // just load the comments normally.
        this.focusCommentId = null;
        this.postDetailCommentsService.loadInitialComments();
      },
    });
  }

  private getRouteFragment(): Observable<string> {
    return this.route.fragment;
  }

  private subscribeToUrlFragmentChanges(): void {
    this.subscriptions$.push(
      this.getRouteFragment().subscribe((fragment: string) => {
        this.focusCommentId = fragment;
      }),
    );
  }

  private subscribeToSessionChanges(): void {
    this.subscriptions$.push(
      this.sessionService.sessionChanges.subscribe(() => {
        this.isAuthenticated = this.sessionService.isSessionActive();
        this.isAdminSession = this.sessionService.isAdminSession();
      }),
    );

    this.isAuthenticated = this.sessionService.isSessionActive();
    this.isAdminSession = this.sessionService.isAdminSession();
  }

  private reloadComments(): void {
    this.loadedComments = makeDefaultLoadedCommentsState();

    if (this.focusCommentId) {
      this.fetchInitialCommentsByPosition(this.focusCommentId);
      return;
    } else {
      this.postDetailCommentsService.loadInitialComments();
    }
  }

  private updateCommentHint(): void {
    const context = this.post.social_context;
    const hintTranslationKey = this.isAuthenticated
      ? 'POSTS_COMMUNITY_HIDDEN_COMMENT_SECTION'
      : 'POSTS_COMMUNITY_HIDDEN_COMMENT_SECTION_UNAUTHORIZED';

    const translate = (key: string | Array<string>, interpolateParams?: TranslateParams) =>
      this.translateService.instant(key, interpolateParams) as string;

    switch (context.entity_type) {
      case SocialContextEntityType.Project:
        this.hiddenCommentHint = translate('POSTS_PROJECT_HIDDEN_COMMENT_SECTION', {
          social_context_name: context.entity_title,
          social_context_type: context.entity_type.toLowerCase(),
        });
        break;

      case SocialContextEntityType.Community:
        this.hiddenCommentHint = translate(hintTranslationKey, {
          social_context_name: context.entity_title,
          type: 'health',
        });
        break;

      case SocialContextEntityType.HealthCommunity:
        this.hiddenCommentHint = translate(hintTranslationKey, {
          social_context_name: context.entity_title,
          type: 'health',
        });
        break;

      case SocialContextEntityType.CharityCommunity:
        this.hiddenCommentHint = translate(hintTranslationKey, {
          social_context_name: context.entity_title,
          type: 'charity',
        });
        break;

      default:
        throw new Error(`Unknown entity type: ${context.entity_type}`);
    }
  }
}
